import { Component, DoCheck, OnDestroy, OnInit } from '@angular/core';
import { Subject, catchError, finalize, of, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '../../core/services/storage.service';
import { PortfolioService } from '../../core/services/api/portfolio.service';
import { PstxAccountService } from '../../core/services/pstx-account.service';
import { AccountService } from '../../core/services/api/account.service';
import { InvestmentStatus } from '../../core/enum/investment-status.enum';
import { InvestmentModel } from '../../core/models/investment/investment.model';
import { EkycStatus } from '../../core/enum/ekyc-status.enum';
import { BusinessesService } from '../../core/services/api/mysec/businesses.service';
import { OrderStatus } from '../../core/enum/mysec/order-status.enum';
import { ContractStatus } from '../../core/enum/mysec/contract-status.enum';
import { OrderFilter } from '../mysec/my-orders/order-filter';
import { DashboardTodoService, countTodoCards } from '../../core/services/dashboard-todo.service';
import {
  UNALLOTTED_CAMPAIGN_STATUSES,
  sumUnallottedEcf,
} from '../my-profile/my-investments/ecf-bucket.util';

/**
 * One slice of the Total Assets donut. Add as many as needed — the chart scales.
 *  - `key`        discriminates which detail layout to render when selected
 *  - `value`      drives the slice size AND the headline figure when selected
 *  - `detailsLink` target of the "View Details" link in the detail state
 *  - `tooltipKey` i18n key explaining what the figure covers (FRC-987)
 *
 * Data sources (wired in ngOnInit):
 *  - cash   → PSTX account wallet (Wallet.balance, which already includes .pendingAmount)
 *             + paid-but-unallotted ECF investments (FRC-991)
 *  - shares → portfolio total indicative value (PortfolioSummary.totalMarketValue)
 */
export interface DonutSegment {
  key: 'cash' | 'shares';
  label: string;
  value: number;
  color: string;
  detailsLink: string;
  tooltipKey: string;
}

/** One drawn slice of the donut — see donutArcs()/computeDonutArcs() below. */
export interface DonutArc {
  index: number;
  color: string;
  dasharray: string;
  dashoffset: number;
}

@Component({
  selector: 'app-my-dashboard',
  templateUrl: './my-dashboard.component.html',
  styleUrls: ['./my-dashboard.component.scss'],
  standalone: false,
})
export class MyDashboardComponent implements OnInit, DoCheck, OnDestroy {
  fullName: string;

  // Donut is fully data-driven: push more segments here and the chart re-scales.
  // Values start at 0 and fill in from the APIs (see ngOnInit).
  // Colors mirror brand-tokens.ts (primary.DEFAULT / secondary.DEFAULT) — the donut
  // renders via [attr.stroke] on an SVG circle, so it needs literal hex, not a CSS class.
  segments: DonutSegment[] = [
    {
      key: 'cash',
      label: 'dashboard.cash',
      value: 0,
      color: '#BF2026',
      detailsLink: '/pstx/account',
      tooltipKey: 'dashboard.donutTooltips.cash',
    },
    {
      key: 'shares',
      label: 'dashboard.shares',
      value: 0,
      color: '#006937',
      detailsLink: '/dashboard/portfolio',
      tooltipKey: 'dashboard.donutTooltips.shares',
    },
  ];

  /** null = Total Assets overview; otherwise the drilled-into segment index. */
  selectedIndex: number | null = null;

  // Per-source loading flags; the donut renders once all of them resolve.
  isCashLoading = true;
  isSharesLoading = true;
  // FRC-991: Cash now has two independent sources (wallet + unallotted ECF), so the
  // donut must wait for this one too or it renders a Cash slice that then jumps.
  isUnallottedEcfLoading = true;

  /** Collapsed by default; the G/L chevron toggles the Unrealised/Realised rows. */
  isBreakdownExpanded = false;

  // --- Detail payloads ---
  // Shares / overview gain-loss (overview G/L == shares G/L: cash has no G/L).
  // totalGainLoss / totalRealisedGainLoss are only populated by /portfolio/detailsGl
  // (see PortfolioSummary model) — unrealised has no dedicated summary field, so it's
  // derived as totalGainLoss - totalRealisedGainLoss.
  totalGainLoss = 0;
  unrealised = 0;
  realised = 0;
  /**
   * GRC-2616: shares are linked to the account but eKYC isn't done, so the holdings
   * are provisional and must stay hidden (value redacted to 0, G/L rows suppressed).
   * Mirrors MyPortfolioComponent, which redacts its summary card on the same flag.
   * The API sets Provisional = !isVerified (eKYC not Verified/NotRequired) — it does
   * NOT zero the figures itself, so every consumer of the summary must redact.
   */
  isSharesProvisional = false;
  /**
   * FRC-1029: the withheld treatment — masked total, lock, no drill-down.
   *
   * Only for shares linked by email (PSTX 2.0 import) and not yet claimed into an ECF
   * account, which the API reports as `summary.hasUnclaimedShares`. `isSharesProvisional`
   * alone is NOT this signal: it is `!isVerified`, so it is also true for a brand-new signup
   * who owns nothing and for an established investor whose eKYC lapsed (Failed, ReKYC,
   * Pending, InProgress) — neither has shares waiting to be claimed.
   *
   * FRC-1043: this used to be inferred client-side as `provisional && holdings.length > 0 &&
   * Cash === 0`. A real ECF/PSTX investor with no cash satisfied all three and was told to
   * claim shares they already own. Everyone else keeps GRC-2616 — shares redacted to 0.
   */
  hasWithheldShares = false;
  // Cash breakdown: accountBalance → wallet.balance, available →
  // wallet.withdrawableBalance, onHold → wallet.pendingAmount less its ECF slice
  // (FRC-1007), unallottedEcf → ECF investments paid for but not yet allotted (FRC-991).
  // FRC-995: `accountBalance` is the GROSS wallet balance — it already contains the
  // on-hold money (the API carves LockedAmount out of Balance, see Wallet.FreeBalance),
  // so it is the total and must never be rendered as "Available". The displayed
  // Available is `available` (balance − hold); accountBalance only feeds the Cash
  // total, see recomputeCash().
  // FRC-1007: `walletEcfHold` is wallet.ecfInvestmentHoldAmount — the slice of the
  // wallet's hold that IS an unallotted ECF investment, and so is also counted in
  // `unallottedEcf`. It is not displayed; it exists to be subtracted exactly once,
  // both from the displayed On Hold row and from the Cash total. See recomputeCash().
  cashDetail = { accountBalance: 0, available: 0, onHold: 0, walletEcfHold: 0, unallottedEcf: 0 };

  // --- Things to do (ECF investment cards) ---
  // Two of the panel's cards are data-driven off the investor's ECF investments:
  //   "Pay for your investments" → investments in PendingPayment
  //   "Refund in progress"       → investments in RefundRequested (not yet fully Refunded)
  // There is no per-InvestmentStatus count endpoint, so we reuse the existing
  // profile-investments list filtered by investmentStatus and count the result.
  // The endpoint returns the FULL filtered list (server-side pagination is disabled),
  // so array length is an accurate total. `take` is a defensive cap only.
  needsEkyc = false; // "Verify your identity" — shown (count 1) when eKYC not done
  activeOrdersCount = 0; // "Active Orders" — open + partially-matched PSTX orders
  pendingPaymentCount = 0;
  refundInProgressCount = 0;

  // Each todo source resolves independently; the empty state must wait for all
  // three so it doesn't flash "all caught up" while counts are still 0 mid-load.
  private ekycLoaded = false;
  private investmentTodosLoaded = false;
  private activeOrdersLoaded = false;

  /**
   * FRC-1029: whether the eKYC to-do card shows.
   *
   * One act (eKYC) → one to-do, and for a withheld shareholder it always reads "Claim your
   * shares" — never "Verify your identity". The donut and this card therefore agree by
   * construction, because both read `hasWithheldShares`.
   *
   * Held back until the withheld verdict is known (`!isLoading` covers the shares loader that
   * feeds it). The profile call reads a cached profile and so almost always wins the race;
   * rendering on it alone painted "Verify your identity" and then rewrote it in place once the
   * portfolio call landed. `needsEkyc` also swallows its own
   * errors to false, so including `hasWithheldShares` keeps the card present when that call
   * fails — a withheld shareholder can never be told shares are waiting with no way to claim.
   */
  get showEkycTodo(): boolean {
    return !this.isLoading && (this.needsEkyc || this.hasWithheldShares);
  }

  /** Header badge: number of to-do cards actually shown. */
  get todoCount(): number {
    return countTodoCards({
      showEkycTodo: this.showEkycTodo,
      pendingPaymentCount: this.pendingPaymentCount,
      activeOrdersCount: this.activeOrdersCount,
      refundInProgressCount: this.refundInProgressCount,
    });
  }

  /**
   * True once every todo source has resolved — gates the empty state.
   *
   * FRC-1029: `showEkycTodo` now waits on the withheld verdict, so this must wait on the
   * same thing (`!isLoading`). Otherwise the panel asserts "You're all caught up" on the
   * other three sources and then pops the Claim-your-shares card in underneath it.
   */
  get todosLoaded(): boolean {
    return (
      this.ekycLoaded && this.investmentTodosLoaded && this.activeOrdersLoaded && !this.isLoading
    );
  }

  private readonly destroy$ = new Subject<void>();

  // --- Donut geometry ---
  readonly radius = 94;
  readonly strokeWidth = 32;
  readonly circumference = 2 * Math.PI * this.radius;
  readonly gap = 4; // thin white divider between slices (flat/butt caps, no lozenge)
  readonly mutedColor = '#e7e7e7'; // divider.DEFAULT — unselected slices when one is drilled into

  /** Donut is "ready" only once every data source has resolved. */
  get isLoading(): boolean {
    return this.isCashLoading || this.isSharesLoading || this.isUnallottedEcfLoading;
  }

  get totalAssets(): number {
    return this.segments.reduce((sum, s) => sum + s.value, 0);
  }

  get selectedSegment(): DonutSegment | null {
    return this.selectedIndex === null ? null : this.segments[this.selectedIndex];
  }

  get title(): string {
    const key = this.selectedSegment ? this.selectedSegment.label : 'dashboard.totalAssets';
    return this.translate.instant(key).toUpperCase();
  }

  /**
   * FRC-987: i18n key for the info tooltip beside the donut heading. Tracks the
   * active view, so one trigger covers all three states the heading can show
   * (Total Assets overview, Shares, Cash).
   */
  get titleTooltipKey(): string {
    return this.selectedSegment
      ? this.selectedSegment.tooltipKey
      : 'dashboard.donutTooltips.totalAssets';
  }

  /** Accessible name for a donut arc, e.g. "View Shares breakdown". */
  segmentAriaLabel(index: number): string {
    const segment = this.segments[index];
    return this.translate.instant('dashboard.a11y.viewSegmentBreakdown', {
      segment: segment ? this.translate.instant(segment.label) : '',
    });
  }

  /** Headline figure: the selected slice's value, or the grand total in overview. */
  get displayValue(): number {
    return this.selectedSegment ? this.selectedSegment.value : this.totalAssets;
  }

  /**
   * Per-slice stroke dash values for an N-segment donut. Rendered by the template's
   * `*ngFor="let arc of donutArcs; trackBy: trackArc"`.
   *
   * FRC-1016: this used to recompute a brand-new array (of brand-new arc objects) on
   * every single call. Angular re-invokes a getter used as an *ngFor source on every
   * change-detection check, so the *ngFor saw new object identities every check and —
   * with no trackBy — tore down and rebuilt every arc (and its click/keydown listeners)
   * every single check, pinning the main thread. It stayed hidden until cash/shares had
   * a value: the empty-state short-circuit inside computeDonutArcs() returns [] while
   * both segments are 0, so a fresh investor never rebuilds anything — the freeze only
   * appeared once a trade left a segment funded. Same failure mode already fixed once in
   * order-list.component.ts (commit 3e290d46, "My Orders table freezes the browser tab
   * when the counter has orders") — this mirrors that fix, plus memoization so this
   * getter itself returns the SAME array/object references (not just the same shape)
   * across change-detection checks where nothing actually changed. Direct test/consumer
   * mutation of segments[].value (see the spec) or selectedIndex is still picked up
   * immediately — the cache key covers exactly what computeDonutArcs() reads.
   */
  get donutArcs(): DonutArc[] {
    // Must cover every field computeDonutArcs() reads below (currently value + color) —
    // an under-inclusive key would silently serve a stale-shaped cached arc.
    const cacheKey =
      this.segments.map((s) => `${s.value}:${s.color}`).join(',') + '|' + this.selectedIndex;
    if (cacheKey !== this._donutArcsCacheKey) {
      this._donutArcsCacheKey = cacheKey;
      this._donutArcsCache = this.computeDonutArcs();
    }
    return this._donutArcsCache;
  }

  private _donutArcsCacheKey: string | null = null;
  private _donutArcsCache: DonutArc[] = [];

  private computeDonutArcs(): DonutArc[] {
    const drawn = this.segments
      .map((seg, index) => ({ seg, index }))
      .filter(({ seg }) => seg.value > 0);

    // Empty state: no arcs — the template's grey track circle covers it.
    if (!drawn.length) {
      return [];
    }

    const total = drawn.reduce((sum, { seg }) => sum + seg.value, 0);
    const gap = drawn.length > 1 ? this.gap : 0;
    const available = this.circumference - drawn.length * gap;
    // Dimming only makes sense when the selected segment is itself on the ring.
    // The mobile tab bar can select an unfunded segment (tapping "Cash" at RM 0),
    // which has no arc — dimming then greys out every drawn slice and the donut
    // reads as disabled.
    const selectionDrawn = drawn.some(({ index }) => index === this.selectedIndex);
    let cursor = 0;

    return drawn.map(({ seg, index }) => {
      const len = (seg.value / total) * available;
      const dimmed = selectionDrawn && this.selectedIndex !== index;
      const arc = {
        index,
        color: dimmed ? this.mutedColor : seg.color,
        dasharray: `${len} ${this.circumference - len}`,
        dashoffset: -cursor,
      };
      cursor += len + gap;
      return arc;
    });
  }

  /** trackBy for the *ngFor above — belt-and-suspenders alongside the memoized getter:
   *  reuses each <circle>'s DOM even on the rare change-detection check that races a
   *  cache invalidation. */
  trackArc(_index: number, arc: DonutArc): number {
    return arc.index;
  }

  selectSegment(index: number): void {
    if (this.hasWithheldShares) return;
    // click the active slice again to return to the overview
    this.selectedIndex = this.selectedIndex === index ? null : index;
  }

  clearSelection(): void {
    this.selectedIndex = null;
  }

  /**
   * Select a segment by key (mobile tab bar). Unlike selectSegment(), this does
   * NOT toggle — tapping the already-active tab is a no-op, as a segmented
   * control should behave. Also decouples the tabs from the segments[] order.
   */
  selectSegmentByKey(key: DonutSegment['key']): void {
    if (this.hasWithheldShares) return;
    const index = this.segments.findIndex((s) => s.key === key);
    this.selectedIndex = index === -1 ? null : index;
  }

  /** Palette lookup by key so the mobile tab dots reuse the segment's own colour. */
  segmentColor(key: DonutSegment['key']): string {
    return this.segments.find((s) => s.key === key)?.color ?? '';
  }

  toggleBreakdown(): void {
    this.isBreakdownExpanded = !this.isBreakdownExpanded;
  }

  private setSegmentValue(key: DonutSegment['key'], value: number): void {
    const seg = this.segments.find((s) => s.key === key);
    if (seg) {
      seg.value = value;
    }
  }

  /**
   * FRC-991: Cash = wallet account balance + paid-but-unallotted ECF.
   * The wallet and the ECF list arrive independently, so each loader writes its own
   * slice of cashDetail and then calls this — whichever resolves second still ends
   * up with the complete figure, and a source that fails leaves its term at 0.
   *
   * FRC-995: the wallet term is the account balance ALONE, not `available + onHold`.
   * Wallet.balance is gross (it already includes everything on hold), so adding
   * onHold to it counted every held ringgit twice and inflated both Cash and Total
   * Assets. Summing the stored balance rather than the two displayed rows also keeps
   * the total exact if the clamp in withdrawableBalance ever bites.
   *
   * FRC-1007: the wallet term is now NET of the overlap with the ECF term. An ECF
   * investment paid online is deposited into the wallet and held there, so it sits in
   * wallet.balance AND in the unallotted-ECF sum (which no longer filters those out —
   * see isUnallottedEcf). Removing the overlap is what keeps the two terms disjoint, so
   * the Cash total is unchanged by that fix and only the split between the On Hold and
   * ECF investment rows moves.
   */
  private recomputeCash(): void {
    const walletTerm = Math.max(0, this.cashDetail.accountBalance - this.ecfOverlap);
    this.setSegmentValue('cash', walletTerm + this.cashDetail.unallottedEcf);
  }

  /**
   * The money counted in BOTH terms of Cash: held in the wallet and also summed from the
   * ECF list. `walletEcfHold` alone is not that figure — it is only what the server says
   * is on hold, and the two sources load independently and can fail independently.
   *
   * Capping it at `unallottedEcf` is what makes this safe: we can only ever have
   * double-counted what the ECF list actually contributed. If that source fails or has
   * not arrived, its term is 0, the overlap is 0, and Cash and the On Hold row keep the
   * gross wallet figures — the documented fail-soft behaviour, rather than silently
   * dropping the hold from a total that never gained it. It also bounds the damage if the
   * server's hold and the list ever disagree, since neither term can be over-subtracted.
   */
  private get ecfOverlap(): number {
    return Math.min(this.cashDetail.walletEcfHold, this.cashDetail.unallottedEcf);
  }

  /**
   * The "On Hold" row as displayed for Cash: the wallet's hold less the ECF slice that
   * the "ECF investment" row beneath it now reports in full. Derived at render rather
   * than written into `cashDetail`, so `cashDetail.onHold` stays the gross figure the
   * wallet reported and the row stays correct however the two sources interleave.
   */
  get onHoldExcludingEcf(): number {
    return Math.max(0, +(this.cashDetail.onHold - this.ecfOverlap).toFixed(2));
  }

  constructor(
    private storageService: StorageService,
    private portfolioService: PortfolioService,
    private pstxAccountService: PstxAccountService,
    private accountService: AccountService,
    private businessesService: BusinessesService,
    private readonly translate: TranslateService,
    private readonly dashboardTodoService: DashboardTodoService,
  ) {}

  /** Last count pushed to DashboardTodoService — avoids re-publishing on every CD check. */
  private publishedTodoCount: number | null = null;

  /**
   * FRC-749: publish the badge count to the layout's nav (sidebar + bottom bar) once every todo
   * source has resolved, and again whenever it changes. Same gate as the empty state, so the nav
   * never shows a count the page itself has not settled on.
   */
  ngDoCheck() {
    if (!this.todosLoaded) return;
    const count = this.todoCount;
    if (count !== this.publishedTodoCount) {
      this.publishedTodoCount = count;
      this.dashboardTodoService?.publish(count);
    }
  }

  ngOnInit() {
    this.fullName = this.storageService.fullName;
    this.loadCash();
    this.loadUnallottedEcf();
    this.loadShares();
    this.loadInvestmentTodos();
    this.loadEkycTodo();
    this.loadActiveOrders();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Cash → cached PSTX wallet stream (emits null first, then the wallet). */
  private loadCash(): void {
    // PstxAccountService swallows wallet errors internally: on failure or a
    // missing wallet it emits `false` here (never errors the wallet stream).
    // Treat that as "no cash" and stop loading so the donut still renders.
    this.pstxAccountService
      .getIsWalletFound()
      .pipe(takeUntil(this.destroy$))
      .subscribe((found) => {
        if (!found) {
          this.isCashLoading = false; // cash segment stays 0
        }
      });

    this.pstxAccountService
      .getWallet()
      .pipe(takeUntil(this.destroy$))
      .subscribe((wallet) => {
        if (!wallet) {
          return;
        }
        // finalize() won't fire (BehaviorSubject never completes) — clear the
        // flag here once the first real wallet arrives.
        this.isCashLoading = false;
        // FRC-995: `available` is the withdrawable figure (balance − hold), reusing
        // Wallet.withdrawableBalance so the dashboard and the withdraw popup can never
        // disagree about what "available" means. `wallet` is a Wallet instance (see
        // WalletService.getWallet), so the getter is present; fall back to the
        // subtraction only if a plain object ever reaches here.
        const accountBalance = wallet.balance ?? 0;
        const onHold = wallet.pendingAmount ?? 0;
        const walletEcfHold = wallet.ecfInvestmentHoldAmount ?? 0;
        this.cashDetail = {
          ...this.cashDetail,
          accountBalance,
          available:
            wallet.withdrawableBalance ?? Math.max(0, +(accountBalance - onHold).toFixed(2)),
          // Gross, as the wallet reported it. FRC-1007: the ECF slice is taken off at
          // render by onHoldExcludingEcf(), not here — the ECF term arrives from a
          // separate request that can fail, and netting it off at write time would leave
          // the row (and Cash) short by a hold the total never gained. `available` is
          // left alone either way: it is the withdrawable figure (balance − the whole
          // hold), and ECF money is not withdrawable just because it is reported on its
          // own row. The three rendered rows still sum to the Cash total.
          onHold,
          walletEcfHold,
        };
        this.recomputeCash();
      });
  }

  /**
   * FRC-991: the ECF half of Cash — investments the investor has paid for but whose
   * shares have not been allotted yet. Legacy payments (bank-in/DuitNow/SWIFT/cheque)
   * never touch the wallet and so are invisible to loadCash() — which is the bug FRC-991
   * fixed. FRC-1007: online payments DO touch the wallet (deposit then hold), and are
   * now included here as well, with loadCash() netting off wallet.ecfInvestmentHoldAmount
   * so neither half is counted twice.
   *
   * The campaign-stage filter is applied server-side (`campaignStatuses` is the one
   * filter GetProfileInvestmentFilterRequest actually honours); payment status is
   * filtered client-side because the endpoint has no investmentStatuses filter —
   * same constraint loadInvestmentTodos() works around.
   *
   * Paging: this endpoint does NOT page server-side today — UsersController passes the
   * filter straight to InvestmentService.GetProfileInvestments, whose query ends in a
   * plain ToListAsync() with no Skip/Take, so page 1 already returns every matching
   * row. A sum must not silently depend on that, so fetchUnallottedEcfPage() is written
   * to be correct either way: it keeps requesting while a FULL page still yields
   * unseen ids, and dedupes by investment id so a server that ignores `currentPage`
   * and re-returns the same rows cannot double-count them.
   *
   * Fail-soft like every other donut source: on error the term stays 0 and the donut
   * still renders rather than blanking the dashboard.
   */
  private loadUnallottedEcf(): void {
    this.fetchUnallottedEcfPage(1, new Map<number, InvestmentModel>());
  }

  /** Page size per request, and a hard stop so a misbehaving server can't loop us. */
  private static readonly ECF_PAGE_SIZE = 100;
  private static readonly ECF_MAX_PAGES = 20;

  private fetchUnallottedEcfPage(page: number, seen: Map<number, InvestmentModel>): void {
    this.accountService
      .getMyInvestments(page, MyDashboardComponent.ECF_PAGE_SIZE, UNALLOTTED_CAMPAIGN_STATUSES)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([])),
      )
      .subscribe((investments) => {
        const rows = investments ?? [];
        const sizeBefore = seen.size;
        rows.forEach((row) => seen.set(row.id, row));

        const pageWasFull = rows.length === MyDashboardComponent.ECF_PAGE_SIZE;
        const yieldedNewRows = seen.size > sizeBefore;
        if (pageWasFull && yieldedNewRows && page < MyDashboardComponent.ECF_MAX_PAGES) {
          this.fetchUnallottedEcfPage(page + 1, seen);
          return;
        }

        this.cashDetail = {
          ...this.cashDetail,
          unallottedEcf: sumUnallottedEcf([...seen.values()]),
        };
        this.recomputeCash();
        this.isUnallottedEcfLoading = false;
      });
  }

  /** Shares → portfolio summary (one-shot HTTP request). */
  private loadShares(): void {
    this.portfolioService
      .getPortfolioGl(1, 20)
      .pipe(
        takeUntil(this.destroy$),
        // FRC-1029: without this a failed call left both flags at their previous value and
        // the error surfaced unhandled — a withheld shareholder would be shown an unmasked
        // total, i.e. the RM 0 claim this ticket exists to remove, via the error path.
        catchError(() => of(null)),
        finalize(() => (this.isSharesLoading = false)),
      )
      .subscribe((data) => {
        // Derive from a completed response only — never leave a previous verdict on screen.
        this.isSharesProvisional = false;
        this.hasWithheldShares = false;
        if (!data) return;

        const summary = data.summary;
        this.isSharesProvisional = !!summary?.provisional;
        this.hasWithheldShares = this.isSharesProvisional && !!summary?.hasUnclaimedShares;

        // GRC-2616: pre-eKYC the whole shares side is redacted — the segment value and
        // the G/L figures all derive from holdings the investor isn't entitled to see yet.
        // Leaving them at 0 keeps the donut rendering (cash only) instead of blanking it.
        if (this.isSharesProvisional) {
          this.setSegmentValue('shares', 0);
          this.totalGainLoss = 0;
          this.realised = 0;
          this.unrealised = 0;
          return;
        }

        this.setSegmentValue('shares', summary?.totalMarketValue ?? 0);
        // Overview G/L == shares G/L (cash has no gain/loss).
        this.totalGainLoss = summary?.totalGainLoss ?? 0;
        this.realised = summary?.totalRealisedGainLoss ?? 0;
        this.unrealised = this.totalGainLoss - this.realised;
      });
  }

  /**
   * Things-to-do ECF cards → counts of the investor's pending-payment and
   * refund-in-progress investments. One filtered list call for both statuses,
   * partitioned client-side (the client-side filter also guards against the
   * server ignoring the status filter). On error both counts stay 0 (cards hide).
   */
  private loadInvestmentTodos(): void {
    const take = 100; // defensive cap; endpoint returns the full filtered list

    this.accountService
      .getMyInvestments(1, take, undefined, [
        InvestmentStatus.PendingPayment,
        InvestmentStatus.RefundRequested,
      ])
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([])),
      )
      .subscribe((investments) => {
        const rows = investments ?? [];
        this.pendingPaymentCount = rows.filter(
          (i) => i.investmentStatus === InvestmentStatus.PendingPayment,
        ).length;
        this.refundInProgressCount = rows.filter(
          (i) => i.investmentStatus === InvestmentStatus.RefundRequested,
        ).length;
        this.investmentTodosLoaded = true;
      });
  }

  /**
   * "Verify your identity" card → shown when the investor still needs eKYC.
   * Must-verify = not Verified and not NotRequired (matches the investing/trading gate).
   * Reads the cached profile (one API call, then cached on AccountService).
   */
  private loadEkycTodo(): void {
    this.accountService
      .profile(false)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
      )
      .subscribe((profile) => {
        this.needsEkyc =
          !!profile && ![EkycStatus.Verified, EkycStatus.NotRequired].includes(profile.ekycStatus);
        this.ekycLoaded = true;
      });
  }

  /**
   * "Active Orders" card → count of the investor's open + partially-matched PSTX
   * orders. No orders-count endpoint exists, so we request the filtered list with
   * take=1 and read the server-side total (totalCount).
   */
  private loadActiveOrders(): void {
    const filter = new OrderFilter();
    filter.take = 1; // only the server-side total is needed
    filter.orderStatuses = [OrderStatus.Open, OrderStatus.PartiallyMatched];
    // The investor orders endpoint applies no server-side status filter on this path
    // (MySecOrderService.InvestorGetMyOrders leaves validOrderStatuses null when isOrderBook),
    // so this must match order-list.component's Manage-tab filter (FRC-833) or expired/cancelled
    // orders leak into this count even though they've dropped off the real orders page.
    filter.orderContractStatus = ContractStatus.Pending;

    this.businessesService
      .getOrderList(filter)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
      )
      .subscribe((res) => {
        this.activeOrdersCount = res?.total ?? 0;
        this.activeOrdersLoaded = true;
      });
  }
}
